// HESHAURA — customer auth store. JWT bearer token in localStorage.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "heshaura_token_v1";
const ME_TIMEOUT_MS = 10000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [authError, setAuthError] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);

  const restore = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    setAuthError("");
    if (!token) {
      setUser(null);
      setReady(true);
      return;
    }
    setReady(false);
    try {
      const r = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` }, timeout: ME_TIMEOUT_MS });
      setUser(r.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setSessionExpired(true);
      } else {
        setAuthError(
          err?.code === "ECONNABORTED"
            ? "The server took too long to respond."
            : status
              ? `The server returned an error (${status}).`
              : "We couldn't reach the HESHAURA server.",
        );
      }
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    restore();
  }, [restore]);

  const saveSession = (data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setSessionExpired(false);
    setAuthError("");
    setUser(data.user);
  };

  const login = async (identifier, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { identifier, password });
    saveSession(data);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await axios.post(`${API}/auth/register`, payload);
    saveSession(data);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, ready, authError, sessionExpired, retryAuth: restore, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
